import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { AssignmentSummaryDto } from '../../../../../../common/general-types/briefs.dto';
import { BriefKind } from '../../assignment-view.helpers';
import { ui } from '../../assignment-ui';

export type StageFilter = 'all' | 'needs' | 0 | 1 | 2 | 3 | 4 | 5;

/** Summary counts; null where only the older filter-stats endpoint answered. */
export type TileSummary = { [K in keyof AssignmentSummaryDto]: number | null };

interface Tile {
  id: Exclude<StageFilter, 'all'>;
  label: string;
  value: number | null;
  of?: number | null;
  sub: string;
  progress?: number;
}

/** The filter card above the table: Needs you, then one tile per stage. */
@Component({
  selector: 'mbt-stage-tiles',
  template: `
    <!-- Each tile draws its own left/top hairline; the card clips the outer ones, so the
         grid lines stay right at 7, 4 and 2 columns. -->
    <div role="group" aria-label="Filter by stage"
      class="grid grid-cols-[1.25fr_repeat(6,minmax(0,1fr))] overflow-hidden {{ ui.card }} @max-[1180px]:grid-cols-4 @max-[680px]:grid-cols-2">
      @for (tile of tiles(); track tile.id) {
        <button type="button" [attr.aria-pressed]="active() === tile.id" (click)="toggle(tile.id)"
          class="relative flex min-w-0 cursor-pointer flex-col gap-1.5 px-4 pt-3.5 pb-[13px] text-left shadow-[-1px_0_0_var(--border-subtle),0_-1px_0_var(--border-subtle)] transition-colors duration-[140ms] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-(--app-accent-ring)"
          [class]="tileClass(tile)">
          <span [class]="tile.id === 'needs' ? ui.kickerBase + ' text-(--app-accent-strong)' : ui.kicker">{{ tile.label }}</span>
          <span class="font-(family-name:--font-display) text-[34px] leading-[0.85] font-extrabold tracking-[-0.02em] tabular-nums"
            [class]="!tile.value ? 'text-(--fg-muted)' : tile.id === 'needs' ? 'text-(--app-accent-strong)' : 'text-(--fg-1)'">{{ tile.value ?? '\u2014' }}@if (tile.of) {<small class="text-[19px] font-bold text-(--fg-muted)"> / {{ tile.of }}</small>}</span>
          @if (tile.progress != null) {
            <span class="mt-0.5 h-1.5 overflow-hidden rounded-[3px] bg-(--bg-alt)" aria-hidden="true">
              <i class="block h-full rounded-[3px] bg-(image:--app-grad) transition-[width] duration-[360ms]" [style.width.%]="tile.progress"></i>
            </span>
          } @else {
            <span class="truncate {{ ui.sub }}">{{ tile.sub }}</span>
          }
        </button>
      }
    </div>
  `,
  host: { style: 'display: contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StageTiles {
  protected readonly ui = ui;

  readonly summary = input<TileSummary | null>(null);
  readonly kind = input<BriefKind>('ugc');
  readonly creatorsNeeded = input<number | null | undefined>(null);
  readonly active = input<StageFilter>('all');

  readonly selected = output<StageFilter>();

  protected readonly tiles = computed<Tile[]>(() => {
    const s = this.summary();
    const n = (value: number | null | undefined) => value ?? 0;
    const sub = (value: number | null | undefined, text: string, fallback: string) =>
      value == null ? fallback : `${value} ${text}`;
    const needed = this.creatorsNeeded() || null;
    const hired = n(s?.hiredOrBeyond);
    const needs = s ? s.needsYou : 0;
    return [
      { id: 'needs', label: 'Needs you', value: needs, sub: needs == null ? 'Count unavailable' : needs ? 'Actions waiting on you' : 'All caught up' },
      { id: 0, label: 'Invited', value: n(s?.invited), sub: sub(s?.invitedWaiting2d, 'waiting 2+ days', 'Waiting to apply') },
      { id: 1, label: 'Applied', value: n(s?.applied), sub: sub(s?.appliedToReview, 'to review', 'Awaiting your review') },
      {
        id: 2, label: 'Hired', value: hired, of: needed, sub: '',
        progress: needed ? Math.min(100, (hired / needed) * 100) : undefined,
      },
      { id: 3, label: 'Started', value: n(s?.started), sub: sub(s?.startedWithNewVideos, 'with new videos', 'Filming') },
      {
        id: 4, label: this.kind() === 'influence' ? 'Posting' : 'Delivered', value: n(s?.delivering),
        sub: this.kind() === 'influence' ? 'Ready to post' : 'Ready to approve',
      },
      { id: 5, label: 'Completed', value: n(s?.completed), sub: 'Paid' },
    ];
  });

  protected tileClass(tile: Tile): string {
    const on = this.active() === tile.id;
    const accentBar = on ? ' before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:bg-(--app-accent)' : '';
    if (tile.id === 'needs') {
      return `bg-(--app-accent-soft)${accentBar}`;
    }
    return (on ? 'bg-(--mba-surface)' : 'hover:bg-(--bg-alt)') + accentBar;
  }

  protected toggle(id: Exclude<StageFilter, 'all'>): void {
    this.selected.emit(this.active() === id ? 'all' : id);
  }
}
